import React, { useState } from 'react';
import { Box, Card, Typography, IconButton, Menu, MenuItem, ListItemIcon, Divider, FormControl, InputLabel, Select } from '@mui/material';
import { GenericCurveChart } from '../GenericCurveChart';
import { GenericBarChart } from '../GenericBarChart';

// import moment from 'moment';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import PrintIcon from '@mui/icons-material/Print';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate, useLocation } from 'react-router-dom';
import { ProjectDetailsSummaryBody } from './ProjectDetailsSummaryBody';
import { ProjectDetailsTable } from './ProjectDetailsTable';
import { ProjectGraphModal } from './ProjectGraphModal';
import { getTypeCode } from './treeUtils';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import DateSelectorButton from '../DateSelectorButton';

interface ProjectDetailsProps {
    projectId: string;
    selectedDate: string;
    setSelectedDate: (value: string) => void;
    selectedScale: '0' | '1' | '2';
    setSelectedScale: (value: '0' | '1' | '2') => void;
    handleHeadClick: (head: string) => void
    activeHead: string;
    details: any;
    graphData: any;
    summary: any;
    lowerDetails: any;
    chartMode: 'line' | 'bar';
    setChartMode: (head: 'line' | 'bar') => void;
}


export const ProjectDetails: React.FC<ProjectDetailsProps> = ({
    handleHeadClick,
    activeHead,
    selectedScale,
    setSelectedScale,
    selectedDate,
    setSelectedDate,
    details,
    graphData,
    summary,
    lowerDetails,
    chartMode,
    setChartMode,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { 
        dates
    } = useSelector((state: RootState) => state.projects);

    const pathParts = location.pathname.split('/');
    const projectId = pathParts[2];
    const currentType = pathParts[3];
    const currentTypeId = pathParts[4];

    const activeMode = !!pathParts[5]
        ? 'means'
        : !!pathParts[3]
            ? pathParts[3]
            : 'projects'

    // console.log('projectId', projectId, 'currentType', currentType, 'currentTypeId', currentTypeId)

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [subMenuEl, setSubMenuEl] = useState<null | HTMLElement>(null);
    const openMenu = Boolean(anchorEl);
    const openSubMenu = Boolean(subMenuEl);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => { setAnchorEl(null); setSubMenuEl(null); };

    const handleSubMenuClick = (event: React.MouseEvent<HTMLElement>) => setSubMenuEl(event.currentTarget);

    const handleNavigateHome = () => {
        navigate(`/resultados-operativos-proyectos/${projectId}${location.search}`);
        handleMenuClose();
    };

    const handleClassSelect = (type: string) => {
        navigate(`/resultados-operativos-proyectos/${projectId}/${type.toLowerCase()}/all${location.search}`);
        handleMenuClose();
    };

    const handleMeansSelect = () => {
        if (currentType && currentTypeId) {
            navigate(`/resultados-operativos-proyectos/${projectId}/${currentType}/${currentTypeId}/means/all${location.search}`);
        }
        handleMenuClose();
    };

    const { labels, progData, valData, realData } = React.useMemo(() => {
        let l: string[] = [];
        let p: number[] = [];
        let v: number[] = [];
        let r: number[] = [];

        if (graphData && graphData.length > 0) {
            graphData.forEach((d: any) => {
                if (!d.EjeY) return;
                const parts = d.EjeY.split('|');
                const key = parts[0]?.trim();
                const values = parts.slice(1).map((val: string) => parseFloat(val) || 0);

                if (key === 'Programado') p = values;
                else if (key === 'Valorizado') v = values;
                else if (key === 'Real') r = values;
                else l = parts;
            });
            if (l.length === 0 && graphData[0]?.EjeX) {
                l = graphData[0].EjeX.split('|');
            }
        }
        return { labels: l, progData: p, valData: v, realData: r };
    }, [graphData]);

    const datasets = [
        {
            label: 'Programado',
            data: progData,
            borderColor: '#2e86c1',
            backgroundColor: 'rgba(46, 134, 193, 0.5)',
            fill: chartMode === 'line'
        },
        {
            label: 'Valorizado',
            data: valData,
            borderColor: '#f1c40f',
            backgroundColor: 'rgba(241, 196, 15, 0.5)',
            fill: chartMode === 'line'
        },
        {
            label: 'Real',
            data: realData,
            borderColor: '#1abc9c',
            backgroundColor: 'rgba(26, 188, 156, 0.5)',
            fill: chartMode === 'line'
        }
    ];

    let titleContent = (
        <Typography variant="subtitle1" fontWeight="bold">
            RO - {summary.Proyecto || ''}
        </Typography>
    );

    if (activeMode !== 'projects' && activeMode !== 'means') {
        titleContent = (
            <Typography variant="subtitle1" fontWeight="bold">
                {currentType.toUpperCase()} - {summary.Descripcion || 'Cargando...'}
            </Typography>
        );
    } else if (activeMode === 'means') {
        titleContent = (
            <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.1, fontSize: '0.9rem' }}>
                    {currentType.toUpperCase()} - {summary.PadreDescripcion || '...'}
                </Typography>
                <Typography variant="caption" sx={{ lineHeight: 1.1, color: 'rgba(255,255,255,0.8)', display: 'block' }}>
                    Recurso: {summary.DesInsumo || 'Cargando...'}
                </Typography>
            </Box>
        );
    }

    const handlePrint = () => {
        handleMenuClose();
        window.print();
    };

    const [showGraphModal, setShowGraphModal] = useState(false);

    const handleGraphClick = () => {
        if (activeMode === 'means') {
            return
        }
        setShowGraphModal(true);
    };

    const modalTypeCode = currentType ? getTypeCode(currentType) : '01';

    // eslint-disable-next-line no-unreachable
    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <style>
                {`
                @media print {
                    @page {
                        size: landscape;
                        margin: 0; /* Hides default browser headers/footers */
                    }
                    body * {
                        visibility: hidden;
                    }
                    #printable-area, #printable-area * {
                        visibility: visible;
                    }
                    #printable-area {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        padding: 15mm; /* Restore visual margins */
                        zoom: 0.85; /* Scale down to fit table */
                    }
                    .no-print {
                        display: none !important;
                    }
                }
                `}
            </style>

            <ProjectGraphModal
                open={showGraphModal}
                onClose={() => setShowGraphModal(false)}
                projectId={projectId}
                typeCode={modalTypeCode}
                typeId={currentTypeId || ''}
                head={activeHead}
                selectedDate={selectedDate}
                selectedScale={selectedScale}
            />

            <Box id="printable-area" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 4, }}>
                <Card sx={{ overflow: 'visible' }}>
                    <Box sx={{ bgcolor: '#007bff', color: 'white', px: 2, py:2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 40 }}>
                        {titleContent}

                        <Box display="flex" alignItems="center" className="no-print">

                            <Box display="flex" alignItems="center" bgcolor="transparent" borderRadius={1} mr={1}>
                                <FormControl size="small">
                                    <InputLabel shrink id="scales-label" style={{color: 'white'}}>
                                        Escala
                                    </InputLabel>
                                    <Select
                                        labelId="scales-label"
                                        id="scales"
                                        // value={scale}
                                        value={selectedScale}
                                        onChange={(event) => {
                                            setSelectedScale(event.target.value as any);
                                        }}
                                        displayEmpty
                                        fullWidth
                                        // className={classes.selectEmpty}
                                        sx={{
                                            '& .MuiSelect-select': {
                                                padding: '4px 8px',
                                            }
                                        }}
                                    >
                                        {/* <MenuItem value="">
                                            <em>Por defecto</em>
                                        </MenuItem> */}
                                        <MenuItem value={'0'}>Mensual</MenuItem>
                                        <MenuItem value={'1'}>Semanal</MenuItem>
                                        <MenuItem value={'2'}>Anual</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box display="flex" alignItems="center" bgcolor="transparent" borderRadius={1} mr={1}>
                                {/* <FormControl size="small" style={{minWidth: '6em'}}>
                                    <InputLabel shrink id="dates-label" style={{color: 'white'}}>
                                        Hasta
                                    </InputLabel>
                                    <Select
                                        labelId="dates-label"
                                        id="dates"
                                        value={selectedDate}
                                        onChange={event => setSelectedDate(event.target.value)}
                                        displayEmpty
                                        fullWidth
                                        // className={classes.selectEmpty}
                                    >
                                        <MenuItem value="">
                                            <em></em>
                                        </MenuItem>
                                        {dates.map((d, i) => (
                                            <MenuItem key={`${i}`} value={d.CodPeriodo}>{d.DesPeriodo}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl> */}
                                {dates.length > 0 && <DateSelectorButton
                                    dates={dates}
                                    selectedDate={selectedDate}
                                    onDateChange={(codigo, descripcion) => {
                                        setSelectedDate(codigo);
                                        console.log(`Seleccionado: ${descripcion}`);
                                    }}
                                    groupBy={selectedScale === '2' ? undefined : "Anio"}
                                    buttonColor="secondary"
                                    modalTitle="Seleccionar período"
                                    placeholder="Elige una fecha"
                                />}
                            </Box>

                            <IconButton size="small" onClick={handleMenuClick} sx={{ color: 'white' }}>
                                <SettingsIcon />
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl}
                                open={openMenu}
                                onClose={handleMenuClose}
                            >
                                <MenuItem onClick={handleNavigateHome} disabled={!currentType}>
                                    <ListItemIcon><HomeIcon fontSize="small" /></ListItemIcon>
                                    Ver proyecto
                                </MenuItem>

                                <MenuItem onClick={handleSubMenuClick} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Box display="flex" alignItems="center">
                                        <Typography>Clasificación</Typography>
                                    </Box>
                                    <ArrowRightIcon />
                                </MenuItem>

                                <MenuItem onClick={handleMeansSelect} disabled={!currentType}>
                                    Recursos
                                </MenuItem>

                                <Divider />
                                <MenuItem onClick={handlePrint}>
                                    <ListItemIcon><PrintIcon fontSize="small" /></ListItemIcon>
                                    Imprimir
                                </MenuItem>
                            </Menu>

                            <Menu
                                anchorEl={subMenuEl}
                                open={openSubMenu}
                                onClose={() => setSubMenuEl(null)}
                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            >
                                <MenuItem onClick={() => handleClassSelect('pc')}>PC Partida de Control</MenuItem>
                                <MenuItem onClick={() => handleClassSelect('de')}>DE Destino Específico</MenuItem>
                                <MenuItem onClick={() => handleClassSelect('fr')}>FR Frente</MenuItem>
                            </Menu>
                        </Box>
                    </Box>
                    <ProjectDetailsSummaryBody
                        summary={summary}
                    />
                </Card>

                <Box sx={{ height: 350, bgcolor: 'white', borderRadius: 1, p: 1, border: '1px solid #e0e0e0' }} /*Data-feature="graph-click"*/ >
                    {chartMode === 'line' ? (
                        <GenericCurveChart labels={labels} datasets={datasets} height={330} title="" options={{ maintainAspectRatio: false, elements: { line: { tension: 0.4, fill: true } }, scales: { y: { beginAtZero: true }, x: { grid: { display: true } } }, onClick: handleGraphClick }} />
                    ) : (
                        <GenericBarChart 
                            labels={labels} 
                            datasets={datasets} 
                            height={330} 
                            title="" 
                            options={{ 
                                maintainAspectRatio: false, 
                                scales: { 
                                    y: { beginAtZero: true }, 
                                    x: { grid: { display: false } 
                                }}, 
                                onClick: () => {}
                            }} />
                    )}
                </Box>


                <ProjectDetailsTable
                    chartMode={chartMode}
                    setChartMode={setChartMode}
                    activeHead={activeHead}
                    handleHeadClick={handleHeadClick}
                    details={details}
                    lowerDetails={lowerDetails}
                />
            </Box>
        </LocalizationProvider>
    );
};